import { useMemo } from 'react';

import { ctw } from '@/common';
import { ReasoningTooltip } from '../../primitives';
import { QuestionMarkTooltip } from '../components/QuestionMarkTooltip';
import { ReportWrapper } from '../components/ReportWrapper';
import { BadgeCritical } from '../components/svg/BadgeCritical';

type DigitalFootprintTag = {
  name: string;
  // Free-text since BAL-6817; unknown levels render gray and sort last.
  level: string;
  // BAL-8159: concise reasoning surfaced as a hover tooltip on the signal.
  reasoning?: string | null;
};

type TagsSectionProps = {
  tags?: DigitalFootprintTag[] | null;
};

const CARD_SCHEME_CONTENT_RISK_TAG_NAME = 'Card-Scheme Content Risk';

export const CriticalTagIcon = () => (
  <span className="inline-flex shrink-0">
    <BadgeCritical variant="simple" />
  </span>
);

const isCardSchemeContentRisk = (tagName: string) => tagName === CARD_SCHEME_CONTENT_RISK_TAG_NAME;

export const getTagStyles = (level: DigitalFootprintTag['level']) => {
  const normalizedLevel = level.toLowerCase();

  switch (normalizedLevel) {
    case 'positive':
      return 'bg-green-100 text-green-700';
    case 'negative':
      return 'bg-red-100 text-red-700';
    case 'moderate':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export const sortTagsBySeverity = (
  tags: DigitalFootprintTag[] | undefined | null,
): DigitalFootprintTag[] => {
  if (!tags) {
    return [];
  }

  const severityOrder: Record<string, number> = {
    negative: 0,
    critical: 0,
    high: 0,
    warning: 1,
    medium: 1,
    moderate: 1,
    positive: 2,
    low: 2,
  };

  return [...tags].sort((a, b) => {
    const aOrder = severityOrder[a.level.toLowerCase()] ?? 999;
    const bOrder = severityOrder[b.level.toLowerCase()] ?? 999;

    return aOrder - bOrder;
  });
};

export const TagsSection: React.FC<TagsSectionProps> = ({ tags }) => {
  const sortedTags = sortTagsBySeverity(tags);

  const tagColumns = useMemo(() => {
    if (sortedTags.length === 0) return [];

    const numColumns = 3;
    const tagsPerColumn = Math.ceil(sortedTags.length / numColumns);

    const columns: DigitalFootprintTag[][] = [];

    for (let i = 0; i < numColumns; i++) {
      const startIndex = i * tagsPerColumn;
      const columnTags = sortedTags.slice(startIndex, startIndex + tagsPerColumn);

      if (columnTags.length > 0) {
        columns.push(columnTags);
      }
    }

    return columns;
  }, [sortedTags]);

  if (!tags || sortedTags.length === 0) {
    return null;
  }

  return (
    <ReportWrapper showTooltip={false}>
      <div className="mb-3 flex items-center gap-1 pt-10">
        <h4 className="text-sm font-semibold text-stone-300">SIGNALS</h4>

        <QuestionMarkTooltip
          text="Key risk indicators representing the main findings of the analysis."
          className="ml-2"
        />
      </div>
      <div className="flex gap-4">
        {tagColumns.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-1 cursor-default flex-col gap-2">
            {column.map((tag, tagIndex) => {
              const isCritical = isCardSchemeContentRisk(tag.name);
              const styles = isCritical ? 'bg-red-200 text-red-800' : getTagStyles(tag.level);
              const hasReasoning = Boolean(tag.reasoning?.trim());

              // One pill definition for both slots: the chip itself, and the tooltip heading
              // that names it. `extraClasses`/`extraProps` carry the differences — the chip
              // truncates and is focusable, the heading does neither.
              const tagPill = (
                extraClasses?: string,
                extraProps?: { title?: string; tabIndex?: number },
              ) => (
                <span
                  className={ctw(
                    'inline-flex items-center gap-1 rounded-sm border-0 px-1.5 py-0.5 text-xs',
                    styles,
                    extraClasses,
                  )}
                  {...extraProps}
                >
                  {isCritical && <CriticalTagIcon />}
                  {tag.name}
                </span>
              );

              return (
                <div key={tagIndex} className="flex">
                  <ReasoningTooltip reasoning={tag.reasoning} title={tagPill()}>
                    {tagPill(ctw('max-w-60 truncate 2xl:max-w-80', hasReasoning && 'cursor-help'), {
                      // Reasoning tooltip supersedes the native name tooltip when present,
                      // so we don't show both on hover.
                      ...(hasReasoning ? {} : { title: tag.name }),
                      // BAL-8159 a11y: focusable only when it has reasoning so keyboard/SR
                      // users can open the tooltip on focus.
                      ...(hasReasoning ? { tabIndex: 0 } : {}),
                    })}
                  </ReasoningTooltip>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </ReportWrapper>
  );
};
